import * as React from "react";

import "./customize-step.scss";

import {isPickPack}                     from "@Common/logic/item/is-pick-pack";
import {cs}                             from "@Common/react/chain-services";
import {consumeContext, provideContext} from "@Common/react/context";
import {fragments}                      from "@Common/react/fragments";
import {Invoke}                         from "@Common/react/invoke";
import {joinJsx}                        from "@Common/react/join-jsx";
import {Load}                           from "@Common/react/load";
import {buildUrlQuery}                  from "@Common/utils/build-url-query";
import {isNotEmpty}                     from "@Common/utils/objects";
import {parseUrlQuery}                  from "@Common/utils/parse-url-query";

import {Layout}              from "../../../layout/layout";
import {CustomizeLayout}     from "../common/customize-layout/customize-layout";
import {loadMailingLists}    from "../common/load-mailing-lists";
import {EmulateAgentOfOrder} from "../product-options/loaders/emulate-agent-of-order";

import { Build3 } from "./build3/build3";
import {CcView}   from "./cc-view/cc-view";

export const CustomizeStep = ({match, location}) => cs(
    ({}, next) => loadMailingLists({next}),
    consumeContext("apis"),
    consumeContext("company"),
    consumeContext("mailingLists"),
    consumeContext("session"),
    consumeContext("routerHistory"),
    ["query", (_, next) => next(parseUrlQuery(location.search))],
    ["renderEmptyLayout", (_, next) => next(({messages, loading, error}) => (
        <Layout className="customize-layout-95r">
            <div className="product-options-step-6rz">
                <div {...{
                    className: "center",
                    ...error ? {
                        "xd-qa-type": "label",
                        "xd-qa-name": "error",
                    } : loading && {
                        "xd-qa-type": "label",
                        "xd-qa-name": "Loading",
                    },
                }}>
                    {joinJsx(messages, () => <br/>)}
                </div>
            </div>
        </Layout>
    ))],
    ["item", ({apis, renderEmptyLayout}, next) => cs(
        ["item", ({}, next) => Load({
            fetch: () => apis.next.item.getItem(match.params.item_id),
            next,
        })],
        ({item}) => !item ? (
            renderEmptyLayout({
                messages: ["Loading Order..."],
                loading: true,
            })
        ) : (
            !item?.sku?.front || ( match.params.component_id && !item?.components?.[match.params.component_id]?.sku?.front ) ? (
                (
                    ( ["failure"].includes(item?.result) && ["inactive"].includes(item?.errors?.template) ) ? 
                    renderEmptyLayout({
                        messages: [ item?.errors?.msg ],
                    }) :
                    renderEmptyLayout({
                        messages: [
                            "There was a problem locating your order.",
                            "Please go back and try again.",
                        ],
                    })
                )
            ) : (
                next(item)
            )
        ),
    )],

    //Emulate agent/branch when proceeding without product options (EX: customize step from pending orders)
    ({item}, next) => item ? (
        EmulateAgentOfOrder({item, next})
    ) : (
        next()
    ),

    // provide the order ID to web chat if webchat is available
    consumeContext("webchat"),
    ({webchat}, next) => fragments(
        next(),
        webchat && Invoke({
            action: () => {
                webchat.setOrderId(match.params.item_id)
            },
        })
    ),

    ["templateOptions", ({item, renderEmptyLayout}, next) => cs(
        consumeContext("templateOptionsCache"),
        ["templateOptions", ({templateOptionsCache}, next) => templateOptionsCache.load({
            key: match.params.component_id ? item?.components?.[match.params.component_id]?.sku : item.sku,
            next,
        })],
        ({templateOptions}) => templateOptions.loading ? (
            renderEmptyLayout({
                messages: ["Loading Template Options..."],
                loading: true,
            })
        ) : (
            (!templateOptions.value) ? (
                renderEmptyLayout({
                    messages: [
                        "There was an error loading the template options.",
                        "Please go back and try again.",
                        "If the issue persists please contact us.",
                    ],
                })
            ) : (
                next(templateOptions.value)
            )
        ),
    )],
    ({templateOptions}, next) => provideContext({templateOptions}, next),

    ["listing", ({apis, item}, next) => Load({
        fetch: async () => {
            if(item.listingUniqueId){
                return await apis.listings.getListing({idUnique: item.listingUniqueId});
            }
            else{
                return {};
            }
        },
        next
    })],

    ["isApprovalAfterProof", ({item, query}, next) => next(
        item.approvalInfo?.location === 'proof' && query?.referrer !== 'approval-requests'
    )],

    ["renderLayout", ({company, item, templateOptions, isApprovalAfterProof}, next) => next(({next, beforeNav}) => CustomizeLayout({
        active: "customization",
        back: {
            link: item ? (
                match.params.component_id ? `/kit-options/order/${item.itemId}`
                 : `/product-options/order/${item.itemId}`
            ) : !templateOptions.categories.frontTemplate.id || !templateOptions.subCategories.frontTemplate.id ? (
                ""
            ) : (
                `/cat/${templateOptions.categories.frontTemplate.id}/sub/${templateOptions.subCategories.frontTemplate.id}`
            ),

            label: item ? (
                match.params.component_id ? "Back to Kit Options" : "Back to Product Options"
            ) : !templateOptions.subCategories.frontTemplate.name ? (
                "Back"
            ) : (
                `Back to ${templateOptions.subCategories.frontTemplate.name}`
            ),
        },
        addressable: item.addressable,
        customizable: !(item.source === 'OIA') && !isPickPack(item),
        isKit: isNotEmpty(item?.components),
        disabled: ["checkout","customization","distribution-options"],
        item,
        beforeNav,
        isApprovalAfterProof,
        next,
    }))],

    ({item, query, company, apis, listing, renderLayout, renderEmptyLayout, templateOptions, isApprovalAfterProof, routerHistory}) =><>
        {templateOptions.proofEngineType === 0 ? cs(

            ["ccView", ({}, next) => (
                CcView({
                    item,
                    referrer: query?.referrer,
                    templateOptions,
                    listing,
                    renderEmptyLayout,
                    title: listing && Object.keys(listing).length !== 0 ? listing?.location?.streetAddress : "No listing Selected",
                    listingId: item.listingUniqueId,
                    isListingsEligible: templateOptions.options.filter((o) => o.devKey === "listings_eligible")[0]?.value,
                    advEditMode: !!templateOptions?.options?.find((o) => o.devKey === "enable_cc_adv_mode")?.value,
                    getItem: async () => {
                        return apis.next.item.getItem(match.params.item_id);
                    },
                    onDone: async () => {
                        if( match.params.component_id ) {
                            routerHistory.push(`/kit-options/order/${item.itemId}` + buildUrlQuery(query));
                        } else if (isApprovalAfterProof) {
                            const result = await apis.next.item.createProofApprovalRequest(item.itemId);
                            if (result.result === 'failure') {
                                alert(result.errors.msg);
                            } else {
                                alert("Order successfully sent for Approval");
                                routerHistory.push(`/account/orders/pending`);
                            }
                        } else {
                            routerHistory.push(`/distribution-options/order/${item.itemId}` + buildUrlQuery(query));
                        }
                    },
                    orderId: match.params.item_id,
                    componentId: match.params.component_id,
                    next,
                })
            )],

            ({ccView}, next) => renderLayout({
                beforeNav: ccView.beforeNav,
                next,
            }),
            ({}, next) => (
                <div className="customize-step-62w cc">
                    {next()}
                </div>
            ),

            ({ccView}) => <>
                {ccView.render()}
            </>,
        ) : cs(
            ({}, next) => renderLayout({
                next,
            }),
            ({}, next) => (
                <div className="customize-step-62w build3">
                    {next()}
                </div>
            ),
            () => (
                <Build3 {...{
                    apis,
                    item,
                    templateOptions,
                    listing,
                    company,
                    match
                }}/>
            )
        )}
    </>
);
